/**
 * Installations public RPC definitions
 *
 * Customer-facing endpoints keyed on installation id (the identifier
 * URL-bound under `/installations/:installationId`). Includes:
 *   - Provisioning status (CLA-72)
 *   - Invoice / payNow / sendInvoice payment flow (CLA-68)
 *   - startProvisioning (CLA-115, Clerk-auth'd)
 *   - Provisioning event ingest + streaming (CLA-115/CLA-220)
 *
 * Per CLA-223 Task 005 this file replaces the prior `onboarding.rpc.ts`
 * and `provisioning.rpc.ts`. Wire-level method names (`onboarding.*`)
 * and payload field names (`onboardingId`) are kept identical during
 * the transitional window — `installationId` and `onboardingId` are
 * 1:1 mapped.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";

import { AuthMiddleware } from "../auth.tags.ts";
import { Forbidden } from "../errors.ts";
import { InstallerTokenMiddleware, InstallerUnauthorized } from "../installer.tags.ts";
import { OnboardingSessionMiddleware } from "../onboarding-session.tags.ts";
import { PublicRateLimitMiddleware } from "../rate-limit.tags.ts";

// ============================================================================
// Error Types
// ============================================================================

/**
 * Installation not found error
 * @since 1.0.0
 */
export class InstallationNotFoundError extends Schema.TaggedError<InstallationNotFoundError>()(
  "InstallationNotFoundError",
  {
    onboardingId: Schema.String,
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Invalid phase for the requested operation
 * @since 1.0.0
 */
export class InstallationInvalidPhaseError extends Schema.TaggedError<InstallationInvalidPhaseError>()(
  "InstallationInvalidPhaseError",
  {
    message: Schema.String,
    expected: Schema.String,
    actual: Schema.String,
  },
) {}

/**
 * Installation Stripe error for payment failures
 * @since 1.0.0
 */
export class InstallationStripeError extends Schema.TaggedError<InstallationStripeError>()(
  "InstallationStripeError",
  {
    message: Schema.String,
  },
) {}

/**
 * Database error during onboarding operations
 * @since 1.0.0
 */
export class InstallationDatabaseError extends Schema.TaggedError<InstallationDatabaseError>()(
  "InstallationDatabaseError",
  {
    message: Schema.optional(Schema.String),
  },
) {}

/**
 * Rate limit exceeded for provisioning event emission.
 *
 * Guards the ingest RPCs so a misbehaving installer loop cannot
 * spam the database or push past the batch-size cap.
 *
 * @since 1.0.0
 */
export class InstallationRateLimitedError extends Schema.TaggedError<InstallationRateLimitedError>()(
  "InstallationRateLimitedError",
  {
    message: Schema.String,
    retryAfterMs: Schema.Number,
  },
) {}

/**
 * Owner-scoped reprovision failed — either the install is not a
 * cloud-vps deployment (Mac has no VPS provision to re-run) or the
 * workflow engine rejected the re-dispatch. Carries the installation id
 * and a human-readable reason so the customer UI can surface why Retry
 * did not take.
 *
 * @since 1.0.0
 */
export class InstallationReprovisionError extends Schema.TaggedError<InstallationReprovisionError>()(
  "InstallationReprovisionError",
  {
    installationId: Schema.String,
    message: Schema.String,
  },
) {}

// ============================================================================
// Request / Response Schemas — Invoice / Payment (CLA-68)
// ============================================================================

/**
 * Request schema for retrieving/creating an onboarding invoice
 * @since 1.0.0
 */
export const InstallationInvoiceRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Response schema for onboarding invoice details
 * @since 1.0.0
 */
export const InstallationInvoiceResponse = Schema.Struct({
  amountCents: Schema.Number,
  description: Schema.String,
  status: Schema.String,
  hostedUrl: Schema.NullOr(Schema.String),
});

/**
 * Request schema for creating a pay-now checkout session
 * @since 1.0.0
 */
export const InstallationPayNowRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Response schema with Stripe checkout URL
 * @since 1.0.0
 */
export const InstallationPayNowResponse = Schema.Struct({
  url: Schema.String,
});

/**
 * Request schema for sending an invoice via email
 * @since 1.0.0
 */
export const InstallationSendInvoiceRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
  billingEmail: Schema.NonEmptyTrimmedString,
});

/**
 * Response schema for send invoice confirmation
 * @since 1.0.0
 */
export const InstallationSendInvoiceResponse = Schema.Struct({
  success: Schema.Boolean,
});

/**
 * Request schema for starting a provider OAuth flow
 * @since 1.0.0
 */
export const InstallationOAuthStartRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
  provider: Schema.NonEmptyTrimmedString,
  // Optional surface keys (gmail, drive, sheets, …) for umbrella providers
  // whose scope list is assembled at runtime from the registry's scopeCatalog.
  surfaces: Schema.optional(Schema.Array(Schema.NonEmptyTrimmedString)),
});

/**
 * Response schema with the provider authorization URL
 * @since 1.0.0
 */
export const InstallationOAuthStartResponse = Schema.Struct({
  authUrl: Schema.String,
});

/**
 * Request schema for completing an OAuth flow via callback.
 *
 * onboardingId is NOT part of the request — it travels via the opaque
 * `state` param (stored server-side with the PKCE verifier at startOAuth
 * time). This lets the OAuth redirect URI stay static per provider so
 * it can be pre-registered in each provider's developer console.
 *
 * @since 1.0.0
 */
export const InstallationOAuthCallbackRequest = Schema.Struct({
  provider: Schema.NonEmptyTrimmedString,
  code: Schema.NonEmptyTrimmedString,
  state: Schema.NonEmptyTrimmedString,
});

/**
 * Response schema acknowledging a successful callback. Returns the
 * recovered onboardingId so the web callback route can redirect the
 * customer back to /onboarding/<id>.
 *
 * @since 1.0.0
 */
export const InstallationOAuthCallbackResponse = Schema.Struct({
  success: Schema.Boolean,
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Request schema for saving an API-key provider secret
 * @since 1.0.0
 */
export const InstallationSaveApiKeyRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
  provider: Schema.NonEmptyTrimmedString,
  apiKey: Schema.NonEmptyTrimmedString,
  metadata: Schema.optional(Schema.Unknown),
});

/**
 * Response schema acknowledging an API-key save
 * @since 1.0.0
 */
export const InstallationSaveApiKeyResponse = Schema.Struct({
  success: Schema.Boolean,
});

/**
 * Request schema for listing connections for an onboarding
 * @since 1.0.0
 */
export const InstallationConnectionsRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Safe public-facing connection summary (never includes tokens).
 * @since 1.0.0
 */
export const InstallationConnectionSummary = Schema.Struct({
  provider: Schema.String,
  // Stable identifier distinguishing multiple accounts of the same
  // provider (Google `sub`, Slack `team_id`, …). Empty string for
  // providers with no per-account concept — UI can use it as a stable
  // React key + for `Disconnect this one` actions.
  accountKey: Schema.String,
  type: Schema.Literal("channel", "file-provider", "connector", "email"),
  status: Schema.Literal("pending", "connected", "failed", "revoked"),
  accountLabel: Schema.NullOr(Schema.String),
  connectedAt: Schema.NullOr(Schema.String),
});

/**
 * Response schema for the connections list
 * @since 1.0.0
 */
export const InstallationConnectionsResponse = Schema.Array(InstallationConnectionSummary);

/**
 * Request schema for the public onboarding state lookup
 * @since 1.0.0
 */
export const InstallationStateRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Safe public-facing onboarding state used by the customer-facing flow.
 * Never exposes pricing, invoice ids, or internal notes.
 * @since 1.0.0
 */
export const InstallationStateResponse = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  customerId: Schema.String,
  installationId: Schema.NullOr(Schema.String),
  phase: Schema.Literal("intake", "connecting", "provisioning", "testing", "invoicing", "complete"),
  channels: Schema.Array(Schema.String),
  emailAccounts: Schema.Array(Schema.String),
  fileProviders: Schema.Array(Schema.String),
  connectors: Schema.Array(Schema.String),
  llmProvider: Schema.String,
  deploymentType: Schema.Literal("local-mac", "cloud-vps"),
});

/**
 * Request schema for advancing from intake → connecting
 *
 * Customer-page fires this on mount. Idempotent by design so browser
 * reloads / multiple tabs don't error out.
 *
 * @since 1.0.0
 */
export const InstallationAdvanceToConnectingRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Response schema after advancing to connecting. Includes the resulting
 * phase so the caller can avoid a follow-up state fetch.
 * @since 1.0.0
 */
export const InstallationAdvanceToConnectingResponse = Schema.Struct({
  success: Schema.Literal(true),
  phase: Schema.String,
});

/**
 * Request schema for the customer-facing LLM provider picker. The wizard's
 * LLM page calls this when the customer flips between OpenAI and Anthropic
 * (before they save a key). Only accepts providers we actually support.
 *
 * @since 1.0.0
 */
export const InstallationSetLlmProviderRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
  llmProvider: Schema.Literal("openai", "anthropic"),
});

/**
 * Response schema for the LLM provider setter.
 * @since 1.0.0
 */
export const InstallationSetLlmProviderResponse = Schema.Struct({
  success: Schema.Literal(true),
  llmProvider: Schema.String,
});

// ============================================================================
// Request / Response Schemas — Installer Config (CLA-73)
// ============================================================================

/**
 * Error raised when installer config has already been issued for this onboarding.
 * @since 1.0.0
 */
export class InstallationInstallerConfigAlreadyIssuedError
  extends Schema.TaggedError<InstallationInstallerConfigAlreadyIssuedError>()(
    "InstallationInstallerConfigAlreadyIssuedError",
    {
      onboardingId: Schema.String,
      message: Schema.String,
    },
  )
{}

/**
 * Error during installer config generation (e.g. decryption failure).
 * @since 1.0.0
 */
export class InstallationInstallerConfigError extends Schema.TaggedError<InstallationInstallerConfigError>()(
  "InstallationInstallerConfigError",
  {
    message: Schema.String,
  },
) {}

/**
 * Request for installer config
 * @since 1.0.0
 */
export const InstallerConfigRequest = Schema.Struct({
  onboardingId: Schema.String,
});

/**
 * Response with installer config including decrypted tokens and callback token.
 * @since 1.0.0
 */
export const InstallerConfigResponse = Schema.Struct({
  llmProvider: Schema.String,
  bundle: Schema.String,
  tokens: Schema.Record({ key: Schema.String, value: Schema.String }),
  tailscaleAuthKey: Schema.String,
  callbackToken: Schema.String,
});

// ============================================================================
// Request / Response Schemas — Post-provisioning tests (CLA-75)
// ============================================================================

/**
 * Error raised when the test runner fails to satisfy a request.
 * @since 1.0.0
 */
export class InstallationTestRpcError extends Schema.TaggedError<InstallationTestRpcError>()(
  "InstallationTestRpcError",
  {
    message: Schema.String,
    phase: Schema.Literal("load", "store", "update", "validate"),
  },
) {}

/**
 * Single test result
 * @since 1.0.0
 */
export const InstallationTestResult = Schema.Struct({
  name: Schema.String,
  status: Schema.Literal("passed", "failed"),
  error: Schema.NullOr(Schema.String),
  timestamp: Schema.String,
});

/**
 * Request to trigger / query tests by onboarding id.
 * @since 1.0.0
 */
export const InstallationTestsRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Ack response returned when a background test run has been scheduled.
 * @since 1.0.0
 */
export const InstallationRunTestsResponse = Schema.Struct({
  triggered: Schema.Literal(true),
});

/**
 * Response returning every stored result for an onboarding.
 * @since 1.0.0
 */
export const InstallationTestResultsResponse = Schema.Array(InstallationTestResult);

/**
 * Response confirming the onboarding advanced to the invoicing phase.
 * @since 1.0.0
 */
export const InstallationConfirmSetupResponse = Schema.Struct({
  success: Schema.Literal(true),
});

// ============================================================================
// OAuth Error Types (CLA-70)
// ============================================================================

/**
 * OAuth error raised by the onboarding OAuth flow.
 * @since 1.0.0
 */
export class InstallationOAuthRpcError extends Schema.TaggedError<InstallationOAuthRpcError>()(
  "InstallationOAuthRpcError",
  {
    message: Schema.String,
    phase: Schema.Literal("pkce", "state", "exchange", "persist", "lookup", "config"),
  },
) {}

/**
 * OAuth state that is missing or expired in Redis.
 * @since 1.0.0
 */
export class InstallationOAuthStateNotFoundRpcError
  extends Schema.TaggedError<InstallationOAuthStateNotFoundRpcError>()(
    "InstallationOAuthStateNotFoundRpcError",
    {
      message: Schema.String,
      state: Schema.String,
    },
  )
{}

/**
 * Raised when the onboarding is not in the "connecting" phase.
 * @since 1.0.0
 */
export class InstallationPhaseRpcError extends Schema.TaggedError<InstallationPhaseRpcError>()(
  "InstallationPhaseRpcError",
  {
    onboardingId: Schema.String,
    currentPhase: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Raised when a caller references a provider that is not in the registry.
 * @since 1.0.0
 */
export class InstallationUnknownProviderRpcError extends Schema.TaggedError<InstallationUnknownProviderRpcError>()(
  "InstallationUnknownProviderRpcError",
  {
    provider: Schema.String,
  },
) {}

/**
 * Raised when a caller uses an API-key provider for OAuth (or vice versa).
 * @since 1.0.0
 */
export class InstallationProviderKindMismatchRpcError
  extends Schema.TaggedError<InstallationProviderKindMismatchRpcError>()(
    "InstallationProviderKindMismatchRpcError",
    {
      provider: Schema.String,
      expected: Schema.Literal("oauth", "apiKey"),
      actual: Schema.Literal("oauth", "apiKey", "pairing"),
    },
  )
{}

/**
 * Raised when the Discord bot pool is empty so a fresh bot cannot be
 * allocated for the onboarding. Surfaced inline on the wizard's Discord
 * row via the per-provider error banner from PR #396.
 *
 * @since 1.0.0
 */
export class InstallationDiscordPoolExhaustedRpcError
  extends Schema.TaggedError<InstallationDiscordPoolExhaustedRpcError>()(
    "InstallationDiscordPoolExhaustedRpcError",
    {
      message: Schema.String,
    },
  )
{}

// ============================================================================
// Provisioning event pipeline schemas (CLA-115 / CLA-220)
// ============================================================================

/**
 * Identifier for the provisioning step an event describes.
 * @since 1.0.0
 */
export const ProvisioningStepSchema = Schema.Literal(
  "bootstrap",
  "network",
  "install",
  "config",
  "health",
  "lockdown",
  "done",
  "frp-token",
  "frpc-config",
  "install-token",
  "phone-home-wait",
  "mark-active",
  "install-token-redeemed",
  "phone-home",
  "install-token-reissued",
  "agent-online",
);

/**
 * Status of the step at the time the event was emitted.
 * @since 1.0.0
 */
export const ProvisioningStatusSchema = Schema.Literal(
  "pending",
  "in_progress",
  "done",
  "failed",
  "stalled",
);

/**
 * Wire shape of an emitted event (producer side — lacks `seq` because
 * the server assigns it).
 * @since 1.0.0
 */
export const EmitEventInput = Schema.Struct({
  step: ProvisioningStepSchema,
  status: ProvisioningStatusSchema,
  message: Schema.NullOr(Schema.String),
  percent: Schema.NullOr(Schema.Number),
  detail: Schema.NullOr(Schema.Unknown),
  occurredAt: Schema.DateFromString,
});

/**
 * Persisted event as it flows back to consumers over the stream.
 * Keyed on `installationId` post-CLA-220.
 * @since 1.0.0
 */
export const ProvisioningEventWire = Schema.Struct({
  id: Schema.String,
  installationId: Schema.String,
  seq: Schema.Number,
  step: ProvisioningStepSchema,
  status: ProvisioningStatusSchema,
  message: Schema.NullOr(Schema.String),
  percent: Schema.NullOr(Schema.Number),
  detail: Schema.NullOr(Schema.Unknown),
  occurredAt: Schema.Date,
  receivedAt: Schema.Date,
});

/**
 * Returned by the single-event emit RPC — just the server-assigned
 * monotonic sequence number.
 * @since 1.0.0
 */
export const EmitEventResponse = Schema.Struct({
  seq: Schema.Number,
});

/**
 * Batched emit payload. `events` is the in-order list; the server
 * assigns contiguous seqs and either persists all or none.
 * @since 1.0.0
 */
export const EmitEventBatchPayload = Schema.Struct({
  events: Schema.Array(EmitEventInput),
});

/**
 * Batched emit response — parallel to the incoming events array.
 * @since 1.0.0
 */
export const EmitEventBatchResponse = Schema.Struct({
  seqs: Schema.Array(Schema.Number),
});

/**
 * Subscribe to the event stream for an installation. Consumers are
 * the wizard / install detail page; auth'd via Clerk session.
 * @since 1.0.0
 */
export const ProvisioningEventStreamRequest = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
});

/**
 * Request to re-run a failed cloud-vps provision for an installation.
 * Owner-scoped: the caller must be a member of the install's customer.
 * @since 1.0.0
 */
export const InstallationReprovisionRequest = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
});

/**
 * Response for a successful reprovision re-dispatch. `installationId`
 * echoes the installation whose provision was re-run; the customer page
 * re-subscribes to the same install's event stream to follow the fresh
 * execution.
 * @since 1.0.0
 */
export const InstallationReprovisionResponse = Schema.Struct({
  installationId: Schema.String,
  deploymentType: Schema.Literal("local-mac", "cloud-vps"),
});

// ============================================================================
// RPC Group
// ============================================================================

// ============================================================================
// startProvisioning schemas (CLA-115)
// ============================================================================

/**
 * Request for `onboarding.startProvisioning` — drop-in replacement for
 * the old `advanceToProvisioning`.
 * @since 1.0.0
 */
export const StartProvisioningRequest = Schema.Struct({
  onboardingId: Schema.NonEmptyTrimmedString,
});

/**
 * Response shape: the installer token (shown once), its expiry
 * timestamp, and a deployment-type-specific `curlCommand` that
 * callers paste into the Mac terminal. Hetzner deployments get
 * `null` — CLA-117 dispatches the cluster workflow server-side.
 * @since 1.0.0
 */
export const StartProvisioningResponse = Schema.Struct({
  installerToken: Schema.String,
  expiresAt: Schema.Date,
  deploymentType: Schema.Literal("local-mac", "cloud-vps"),
  curlCommand: Schema.NullOr(Schema.String),
});

/**
 * Installations public RPC group
 *
 * Hosts every customer-facing RPC keyed on installation id. Wire-level
 * method names retain the historical `onboarding.*` prefix and the
 * payload field name `onboardingId` so this rename is transparent to
 * the on-the-wire protocol (CLA-223 Task 005); the URL value carried
 * as `installationId` is forwarded as-is.
 *
 *   - onboarding.invoice                    (CLA-68)
 *   - onboarding.payNow                     (CLA-68)
 *   - onboarding.sendInvoice                (CLA-68)
 *   - onboarding.emitProvisioningEvent      (CLA-115)
 *   - onboarding.emitProvisioningEventBatch (CLA-115)
 *   - installation.provisioningEventStream  (CLA-220)
 *
 * @since 1.0.0
 */
export class InstallationsPublicRpcs extends RpcGroup.make(
  Rpc.make("onboarding.invoice", {
    payload: InstallationInvoiceRequest,
    success: InstallationInvoiceResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationStripeError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.payNow", {
    payload: InstallationPayNowRequest,
    success: InstallationPayNowResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationStripeError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.sendInvoice", {
    payload: InstallationSendInvoiceRequest,
    success: InstallationSendInvoiceResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationStripeError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.oauthStart", {
    payload: InstallationOAuthStartRequest,
    success: InstallationOAuthStartResponse,
    error: Schema.Union(
      InstallationOAuthRpcError,
      InstallationPhaseRpcError,
      InstallationNotFoundError,
      InstallationUnknownProviderRpcError,
      InstallationProviderKindMismatchRpcError,
      InstallationDiscordPoolExhaustedRpcError,
      InstallationDatabaseError,
      Forbidden,
    ),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.oauthCallback", {
    payload: InstallationOAuthCallbackRequest,
    success: InstallationOAuthCallbackResponse,
    error: Schema.Union(
      InstallationOAuthRpcError,
      InstallationOAuthStateNotFoundRpcError,
      InstallationUnknownProviderRpcError,
      InstallationProviderKindMismatchRpcError,
      InstallationDiscordPoolExhaustedRpcError,
    ),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.saveApiKey", {
    payload: InstallationSaveApiKeyRequest,
    success: InstallationSaveApiKeyResponse,
    error: Schema.Union(
      InstallationOAuthRpcError,
      InstallationPhaseRpcError,
      InstallationNotFoundError,
      InstallationUnknownProviderRpcError,
      InstallationProviderKindMismatchRpcError,
      InstallationDatabaseError,
      Forbidden,
    ),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.connections", {
    payload: InstallationConnectionsRequest,
    success: InstallationConnectionsResponse,
    error: Schema.Union(InstallationOAuthRpcError, InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.state", {
    payload: InstallationStateRequest,
    success: InstallationStateResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.startProvisioning", {
    payload: StartProvisioningRequest,
    success: StartProvisioningResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationInvalidPhaseError, InstallationDatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("onboarding.advanceToConnecting", {
    payload: InstallationAdvanceToConnectingRequest,
    success: InstallationAdvanceToConnectingResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.setLlmProvider", {
    payload: InstallationSetLlmProviderRequest,
    success: InstallationSetLlmProviderResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.installerConfig", {
    payload: InstallerConfigRequest,
    success: InstallerConfigResponse,
    error: Schema.Union(
      InstallationNotFoundError,
      InstallationInstallerConfigAlreadyIssuedError,
      InstallationInstallerConfigError,
      InstallationDatabaseError,
      Forbidden,
    ),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.runTests", {
    payload: InstallationTestsRequest,
    success: InstallationRunTestsResponse,
    error: Schema.Union(InstallationTestRpcError, InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.testResults", {
    payload: InstallationTestsRequest,
    success: InstallationTestResultsResponse,
    error: Schema.Union(InstallationTestRpcError, InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.confirmSetup", {
    payload: InstallationTestsRequest,
    success: InstallationConfirmSetupResponse,
    error: Schema.Union(InstallationTestRpcError, InstallationNotFoundError, InstallationDatabaseError, Forbidden),
  }).middleware(OnboardingSessionMiddleware).middleware(PublicRateLimitMiddleware),
  Rpc.make("onboarding.emitProvisioningEvent", {
    payload: EmitEventInput,
    success: EmitEventResponse,
    error: Schema.Union(InstallerUnauthorized, InstallationDatabaseError, InstallationRateLimitedError),
  }).middleware(InstallerTokenMiddleware),
  Rpc.make("onboarding.emitProvisioningEventBatch", {
    payload: EmitEventBatchPayload,
    success: EmitEventBatchResponse,
    error: Schema.Union(InstallerUnauthorized, InstallationDatabaseError, InstallationRateLimitedError),
  }).middleware(InstallerTokenMiddleware),
  Rpc.make("installation.provisioningEventStream", {
    payload: ProvisioningEventStreamRequest,
    success: ProvisioningEventWire,
    error: Schema.Union(InstallationNotFoundError, InstallationDatabaseError),
    stream: true,
  }).middleware(AuthMiddleware),
  Rpc.make("installation.reprovision", {
    payload: InstallationReprovisionRequest,
    success: InstallationReprovisionResponse,
    error: Schema.Union(InstallationNotFoundError, InstallationReprovisionError, InstallationDatabaseError),
  }).middleware(AuthMiddleware),
) {}
